import React from 'react';
import { Mic, BookOpen, Sparkles, TrendingUp, Calendar, Award, ArrowRight, Clock, Play, Zap, Lightbulb, CheckCircle } from 'lucide-react';
type HomeDesignAProps = {
  onNavigate?: (page: string) => void;
  onViewStory?: (storyId: number) => void;
};
export function HomeDesignA({
  onNavigate,
  onViewStory
}: HomeDesignAProps) {
  const quickStats = [{
    label: 'Total Stories',
    value: '5',
    change: '+2 this week',
    icon: BookOpen,
    color: 'green'
  }, {
    label: 'Storybooks',
    value: '5',
    change: '+1 this week',
    icon: Sparkles,
    color: 'purple'
  }, {
    label: 'Recording Time',
    value: '47m',
    change: '+12m this week',
    icon: Clock,
    color: 'blue'
  }, {
    label: 'Credits',
    value: '35',
    change: '15 remaining',
    icon: Zap,
    color: 'amber'
  }];
  const goals = [{
    title: 'Record 5 stories this month',
    current: 3,
    target: 5,
    color: 'green'
  }, {
    title: 'Create your first storybook',
    current: 0,
    target: 1,
    color: 'purple'
  }, {
    title: 'Invite a family member',
    current: 0,
    target: 1,
    color: 'blue'
  }];
  const suggestions = [{
    id: 1,
    title: 'Create a storybook from "Summer at the Lake House"',
    description: 'This story has great visuals - perfect for an illustrated book!',
    action: 'Create Now',
    icon: Sparkles,
    color: 'purple'
  }, {
    id: 2,
    title: "You haven't recorded in 5 days",
    description: 'Try this prompt: "Tell me about your first job"',
    action: 'Start Recording',
    icon: Mic,
    color: 'green'
  }, {
    id: 3,
    title: 'Add AI summary to "Dad\'s War Stories"',
    description: 'Get an instant summary and key moments',
    action: 'Generate Summary',
    icon: Lightbulb,
    color: 'amber'
  }];
  const activityFeed = [{
    id: 1,
    text: 'You recorded "Summer Memories"',
    time: '2 hours ago',
    icon: Mic,
    color: 'green'
  }, {
    id: 2,
    text: 'Storybook "Achilles" completed',
    time: 'Yesterday',
    icon: Sparkles,
    color: 'purple'
  }, {
    id: 3,
    text: 'Sarah viewed "Family Recipe"',
    time: '2 days ago',
    icon: Play,
    color: 'blue'
  }, {
    id: 4,
    text: 'You earned "Storyteller" badge',
    time: '3 days ago',
    icon: Award,
    color: 'amber'
  }];
  const recentStories = [{
    id: 1,
    title: "Grandma's Secret Recipe",
    thumbnail: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=400&h=300&fit=crop',
    duration: '8:24',
    status: 'Ready for storybook',
    statusColor: 'green'
  }, {
    id: 2,
    title: 'Our First Road Trip',
    thumbnail: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=400&h=300&fit=crop',
    duration: '12:35',
    status: 'New',
    statusColor: 'blue'
  }, {
    id: 3,
    title: 'Summer at the Lake House',
    thumbnail: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop',
    duration: '6:18',
    status: 'Needs summary',
    statusColor: 'amber'
  }];
  return <div className="flex-1 bg-gray-100 min-h-screen" data-id="element-774">
      <div className="px-8 py-8" data-id="element-775">
        {/* Hero CTA Section */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-3xl p-12 mb-8 shadow-xl" data-id="element-776">
          <div className="max-w-3xl" data-id="element-777">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-4" data-id="element-778">
              🔥 7 day recording streak!
            </div>
            <h1 className="text-5xl font-bold text-white mb-4" data-id="element-779">
              What will you create today?
            </h1>
            <p className="text-green-100 text-xl mb-8" data-id="element-780">
              You're 2 stories away from creating your first collection
            </p>
            <div className="flex flex-wrap gap-4" data-id="element-781">
              <button className="px-8 py-4 bg-white text-green-600 font-semibold rounded-xl hover:bg-green-50 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center gap-2" data-id="element-782">
                <Mic className="w-5 h-5" data-id="element-783" />
                Record Story
              </button>
              <button className="px-8 py-4 bg-green-700 text-white font-semibold rounded-xl hover:bg-green-800 transition-all flex items-center gap-2" data-id="element-784">
                <Sparkles className="w-5 h-5" data-id="element-785" />
                Create Storybook
              </button>
              <button className="px-8 py-4 bg-green-700 text-white font-semibold rounded-xl hover:bg-green-800 transition-all flex items-center gap-2" data-id="element-786">
                <Lightbulb className="w-5 h-5" data-id="element-787" />
                Get Inspired
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-788">
          {quickStats.map(stat => <div key={stat.label} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-789">
              <div className="flex items-center justify-between mb-4" data-id="element-790">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${stat.color}-100`} data-id="element-791">
                  <stat.icon className="w-5 h-5" data-id="element-792" />
                </div>
                <TrendingUp className="w-4 h-4 text-green-600" data-id="element-793" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1" data-id="element-794">
                {stat.value}
              </div>
              <div className="text-sm text-gray-600 mb-1" data-id="element-795">{stat.label}</div>
              <div className="text-xs text-green-600 font-medium" data-id="element-796">
                {stat.change}
              </div>
            </div>)}
        </div>

        {/* Smart Suggestions */}
        <div className="mb-8" data-id="element-797">
          <div className="flex items-center gap-2 mb-4" data-id="element-798">
            <Sparkles className="w-6 h-6 text-purple-600" data-id="element-799" />
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-800">For You Today</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-801">
            {suggestions.map(suggestion => <div key={suggestion.id} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer" data-id="element-802">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${suggestion.color}-100 mb-4`} data-id="element-803">
                  <suggestion.icon className={`w-6 h-6 text-${suggestion.color}-600`} data-id="element-804" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2" data-id="element-805">
                  {suggestion.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-806">
                  {suggestion.description}
                </p>
                <button className={`w-full py-3 bg-${suggestion.color}-500 text-white font-medium rounded-xl hover:bg-${suggestion.color}-600 transition-colors`} data-id="element-807">
                  {suggestion.action}
                </button>
              </div>)}
          </div>
        </div>

        {/* Two Column Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-808">
          {/* Activity Feed */}
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-809">
            <div className="flex items-center gap-2 mb-6" data-id="element-810">
              <Clock className="w-5 h-5 text-gray-600" data-id="element-811" />
              <h3 className="text-xl font-semibold text-gray-900" data-id="element-812">
                Recent Activity
              </h3>
            </div>
            <div className="space-y-4" data-id="element-813">
              {activityFeed.map(item => <div key={item.id} className="flex items-start gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-814">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center bg-${item.color}-100 flex-shrink-0`} data-id="element-815">
                    <item.icon className={`w-5 h-5 text-${item.color}-600`} data-id="element-816" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-817">
                    <p className="text-sm font-medium text-gray-900" data-id="element-818">
                      {item.text}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-819">{item.time}</p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Goals & Progress */}
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-820">
            <div className="flex items-center gap-2 mb-6" data-id="element-821">
              <Award className="w-5 h-5 text-gray-600" data-id="element-822" />
              <h3 className="text-xl font-semibold text-gray-900" data-id="element-823">
                Goals & Progress
              </h3>
            </div>
            <div className="space-y-6" data-id="element-824">
              {goals.map((goal, index) => <div key={index} data-id="element-825">
                  <div className="flex items-center justify-between mb-2" data-id="element-826">
                    <span className="text-sm font-medium text-gray-900" data-id="element-827">
                      {goal.title}
                    </span>
                    <span className="text-sm text-gray-600" data-id="element-828">
                      {goal.current}/{goal.target}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-829">
                    <div className={`h-full bg-${goal.color}-500 rounded-full transition-all duration-500`} style={{
                  width: `${goal.current / goal.target * 100}%`
                }} data-id="element-830" />
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* Recent Stories */}
        <div className="mb-8" data-id="element-831">
          <div className="flex items-center justify-between mb-6" data-id="element-832">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-833">Recent Stories</h2>
            <button className="text-sm font-medium text-green-600 hover:text-green-700 flex items-center gap-1" data-id="element-834">
              View all
              <ArrowRight className="w-4 h-4" data-id="element-835" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-836">
            {recentStories.map(story => <div key={story.id} onClick={() => onViewStory?.(story.id)} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer hover:-translate-y-1" data-id="element-837">
                <div className="relative aspect-video overflow-hidden" data-id="element-838">
                  <img src={story.thumbnail} alt={story.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-839" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-840">
                    <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center" data-id="element-841">
                      <Play className="w-6 h-6 text-gray-900 ml-0.5" data-id="element-842" />
                    </div>
                  </div>
                  <div className="absolute top-3 left-3" data-id="element-843">
                    <span className={`px-3 py-1 bg-${story.statusColor}-500 text-white text-xs font-medium rounded-full`} data-id="element-844">
                      {story.status}
                    </span>
                  </div>
                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/75 text-white text-xs font-medium rounded backdrop-blur-sm" data-id="element-845">
                    {story.duration}
                  </div>
                </div>
                <div className="p-4" data-id="element-846">
                  <h3 className="font-semibold text-gray-900 line-clamp-1" data-id="element-847">
                    {story.title}
                  </h3>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}